import { useDispatch } from 'react-redux';
import { entries, values } from 'lodash';
import {
  bindActionCreators,
  configureStore,
  createSlice,
} from '@reduxjs/toolkit';
import { momentTimezone } from '../components/utils/dateUtils';
import {
  TTab,
  TSettingsTab,
  TInput,
  TTabToUpdate,
} from '../components/tabs/type';
import {
  fetchAppointments,
  getData,
  getDataCount,
  getDataDetail,
  getDocumentList,
  getInputs,
  getRolesList,
  getTabs,
  getUserRoleContext,
  getUsersList,
  getNotifs,
  getActionsList,
} from './asyncActions';
import { theme } from '../theme';

const localToken = localStorage.getItem('token') || '';

const MAX_MOBILE_SIZE = 480;
const MAX_TABLET_SIZE = 1200;

export const initialState = {
  modalType: '',
  modalData: undefined as any,
  token: localToken,
  isEditMode: false,
  userRole: {} as Record<string, string[]>,
  isAdmin: false,
  isSuper: false,
  login: '',
  isHovered: localStorage.getItem('isMenuOpen') === 'true' || false,
  showNbLine: true,
  isPlanning: false,
  isDuplicate: false,
  planningAddress: '',
  isPlanningEdit: false,
  appointments: {} as any,
  user: {} as Record<string, any>,
  theme,
  isMobile: window.innerWidth < MAX_MOBILE_SIZE,
  isTablet:
    window.innerWidth >= MAX_MOBILE_SIZE && window.innerWidth < MAX_TABLET_SIZE,
  calendarCurrentDay: momentTimezone().toISOString(),
  notifs: [] as Record<string, any>[],

  // custom action
  isOpen: false,
  filterActive: false,

  tabs: [] as TTab[],
  tabTitles: {} as Record<string, string>,
  settingsTabs: [] as TSettingsTab[],
  planningTab: {} as TTab,
  duplicateTab: {} as TTab,
  userList: [] as any[],
  roleList: [] as any[],
  dataInputs: [] as TInput[],
  documentList: [] as any[],
  actionsList: [] as {
    name: string;
    list: { name_id: string; value: any }[];
    pages?: string[];
    roles?: string[];
  }[],
  data: [] as any[],
  noFilter: false as boolean,
  dataCount: {} as Record<string, number>,
  currentTab: undefined as TTab | undefined,
  tabToUpdate: undefined as TTabToUpdate | undefined,
  currentColumnId: '',
  rowData: {} as Record<string, any>,
  filesStatus: {} as Record<string, boolean>,
  filesComms: {} as Record<string, string>,
  appointsHistory: {} as Record<string, any>,
  rowHistory: {} as Record<string, any>,
  rowSelected: [] as string[],
  globalFilter: '' as string,
  columnFilters: [] as Record<string, any>[],

  pendings: {} as Record<string, number>,
  triggerDistance: false,
};

const stateNames = [
  'setModalType',
  'setModalData',
  'setToken',
  'setIsEditMode',
  'setUserRole',
  'setIsAdmin',
  'setIsSuper',
  'setUser',
  'setLogin',
  'setShowNbLine',
  'setIsPlanning',
  'setIsDuplicate',
  'setPlanningAddress',
  'setIsPlanningEdit',
  'setAppointments',
  'setIsMobile',
  'setIsTablet',
  'setCalendarCurrentDay',
  'setTheme',
  'setNotifs',

  'setTabs',
  'setTabTitles',
  'setSettingsTabs',
  'setPlanningTab',
  'setDuplicateTab',
  'setUserList',
  'setRoleList',
  'setDataInputs',
  'setDocumentList',
  'setData',
  'setNoFilter',
  'setDataCount',
  'setCurrentTab',
  'setTabToUpdate',
  'setCurrentColumnId',
  'setRowData',
  'setAppointsHistory',
  'setRowHistory',
  'setRowSelected',
  'setGlobalFilter',
  'setColumnFilters',
  'setFilesStatus',
  'setFilesComms',
  'setTriggerDistance',
] as const;

const customActions = [
  'onToggle',
  'onClose',
  'onOpen',
  'toggleFilter',
  'onNavOpen',
  'toggleEditMode',
] as const;

export type TState = typeof initialState;
export type TActions = Record<
  (typeof stateNames)[number] | (typeof customActions)[number],
  any
>;

const actionsList = stateNames.reduce(
  (prev, curr) => ({
    ...prev,
    [curr]: (state: any, { payload }: any) => {
      const key: any = curr.replace(/^set/gi, '');
      const [first, ...word] = key;
      const newKey = `${first.toLowerCase()}${word.join('')}` as keyof TState;

      if (typeof payload === 'function') {
        state[newKey] = payload(state[newKey]);
      } else {
        state[newKey] = payload;
      }
    },
  }),
  {} as Record<(typeof stateNames)[number], any>,
);

const extras = {
  fetchAppointments,
  getTabs,
  getInputs,
  getDocumentList,
  getDataCount,
  getData,
  getDataDetail,
  getUserRoleContext,
  getRolesList,
  getUsersList,
  getNotifs,
  getActionsList,
};

const globalSlice = createSlice({
  name: 'global',
  initialState: initialState,
  reducers: {
    ...actionsList,
    setToken: (state: TState, { payload: t }: any) => {
      if (!t) {
        localStorage.removeItem('currentTab');
      }

      localStorage.setItem('token', t);
      state.token = t;
    },
    onClose: (state: TState) => {
      state.isOpen = false;
    },
    onOpen: (state: TState) => {
      state.isOpen = true;
    },
    toggleFilter: (state: TState) => {
      state.filterActive = !state.filterActive;
    },
    onNavOpen: (state: TState) => {
      state.isHovered = true;
    },
    onToggle: (state: TState) => {
      state.isHovered = !state.isHovered;
    },
    toggleEditMode: (state: TState) => {
      state.isEditMode = !state.isEditMode;
    },
  },
  extraReducers: (builder: any) => {
    values(extras).forEach((v) => {
      builder.addCase(v.pending, (state: TState, action: any) => {
        state.pendings[action.type] = state.pendings[action.type]
          ? state.pendings[action.type] + 1
          : 1;
      });
      builder.addCase(v.fulfilled, (state: TState, action: any) => {
        entries(action.payload).forEach(([k, v]) => ((state as any)[k] = v));
        state.pendings[action.type.replace('/fulfilled', '/pending')] = 0;
      });
      builder.addCase(v.rejected, (state: TState, action: any) => {
        state.pendings[action.type.replace('/rejected', '/pending')] = 0;
      });
    });
  },
});

export const actions: TActions = globalSlice.actions;

export const useActions = () => {
  const dispatch = useDispatch();

  const asyncActions = entries(extras).reduce(
    (prev, [k, v]: any) => ({
      ...prev,
      [k]: (props: any) => dispatch(v(props)),
    }),
    {} as Record<keyof typeof extras, any>,
  );

  return { ...bindActionCreators(actions, dispatch), ...asyncActions };
};

const store = configureStore({
  reducer: globalSlice.reducer,
});

export default store;
